import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useLanguage } from '../../context/LanguageContext';
import { useMarketingCopy } from '../../context/MarketingCopyContext';
import { en } from '../../translations/en';
import { fr } from '../../translations/fr';
import { faqDefaults } from '../../constants/faqDefaults';

const API = process.env.REACT_APP_API_URL || '/api';

const MARKETING_FIELDS = {
  home: [
    { key: 'hero.subtitle', label: 'Hero Eyebrow', group: 'Hero Section' },
    { key: 'hero.description', label: 'Hero Description', group: 'Hero Section', multiline: true },
    { key: 'hero.bookButton', label: 'Hero Book Button', group: 'Hero Section' },
    { key: 'hero.servicesButton', label: 'Hero Services Button', group: 'Hero Section' },
    { key: 'stats.years', label: 'Stats — Years Value', group: 'Statistics Bar' },
    { key: 'stats.yearsLabel', label: 'Stats — Years Label', group: 'Statistics Bar' },
    { key: 'stats.vehicles', label: 'Stats — Vehicles Value', group: 'Statistics Bar' },
    { key: 'stats.vehiclesLabel', label: 'Stats — Vehicles Label', group: 'Statistics Bar' },
    { key: 'stats.satisfaction', label: 'Stats — Satisfaction Value', group: 'Statistics Bar' },
    { key: 'stats.satisfactionLabel', label: 'Stats — Satisfaction Label', group: 'Statistics Bar' },
    { key: 'stats.technicians', label: 'Stats — Technicians Value', group: 'Statistics Bar' },
    { key: 'stats.techniciansLabel', label: 'Stats — Technicians Label', group: 'Statistics Bar' },
    { key: 'services.subtitle', label: 'Services Section Eyebrow', group: 'Services Preview' },
    { key: 'whyChoose.subtitle', label: 'Why Choose Eyebrow', group: 'Why Choose Us' },
    { key: 'whyChoose.description', label: 'Why Choose Description', group: 'Why Choose Us', multiline: true },
    { key: 'whyChoose.button', label: 'Why Choose Button', group: 'Why Choose Us' },
    { key: 'whyChoose.certified.title', label: 'Certified Title', group: 'Why Choose Us' },
    { key: 'whyChoose.certified.desc', label: 'Certified Description', group: 'Why Choose Us', multiline: true },
    { key: 'whyChoose.pricing.title', label: 'Pricing Title', group: 'Why Choose Us' },
    { key: 'whyChoose.pricing.desc', label: 'Pricing Description', group: 'Why Choose Us', multiline: true },
    { key: 'whyChoose.parts.title', label: 'Parts Title', group: 'Why Choose Us' },
    { key: 'whyChoose.parts.desc', label: 'Parts Description', group: 'Why Choose Us', multiline: true },
    { key: 'whyChoose.inspection.title', label: 'Inspection Title', group: 'Why Choose Us' },
    { key: 'whyChoose.inspection.desc', label: 'Inspection Description', group: 'Why Choose Us', multiline: true },
    { key: 'reviews.subtitle', label: 'Reviews Section Eyebrow', group: 'Reviews' },
    { key: 'cta.subtitle', label: 'CTA Eyebrow', group: 'Call to Action' },
    { key: 'cta.description', label: 'CTA Description', group: 'Call to Action', multiline: true },
    { key: 'cta.button', label: 'CTA Button Text', group: 'Call to Action' },
  ],
  services: [
    { key: 'subtitle', label: 'Page Eyebrow', group: 'Header' },
    { key: 'description', label: 'Page Description', group: 'Header', multiline: true },
    { key: 'dontSee', label: 'Bottom Message', group: 'Footer', multiline: true },
    { key: 'scheduleButton', label: 'Schedule Button', group: 'Footer' },
  ],
  about: [
    { key: 'subtitle', label: 'Page Eyebrow', group: 'Header' },
    { key: 'mission.title', label: 'Mission Title', group: 'Mission' },
    { key: 'mission.text', label: 'Mission Text', group: 'Mission', multiline: true },
    { key: 'team.title', label: 'Team Title', group: 'Team' },
    { key: 'team.text', label: 'Team Text', group: 'Team', multiline: true },
    { key: 'values.quality', label: 'Quality Title', group: 'Values' },
    { key: 'values.qualityText', label: 'Quality Description', group: 'Values', multiline: true },
    { key: 'values.transparency', label: 'Transparency Title', group: 'Values' },
    { key: 'values.transparencyText', label: 'Transparency Description', group: 'Values', multiline: true },
    { key: 'values.expertise', label: 'Expertise Title', group: 'Values' },
    { key: 'values.expertiseText', label: 'Expertise Description', group: 'Values', multiline: true },
    { key: 'values.community', label: 'Community Title', group: 'Values' },
    { key: 'values.communityText', label: 'Community Description', group: 'Values', multiline: true },
    { key: 'visit', label: 'Visit Title', group: 'Visit Section' },
    { key: 'visitText', label: 'Visit Description', group: 'Visit Section', multiline: true },
  ],
  contact: [
    { key: 'subtitle', label: 'Page Eyebrow', group: 'Header' },
    { key: 'form.title', label: 'Form Title', group: 'Contact Form' },
    { key: 'phone1', label: 'Phone Number 1', group: 'Business Info' },
    { key: 'phone2', label: 'Phone Number 2', group: 'Business Info' },
  ],
  faq: [
    { key: 'subtitle', label: 'Page Eyebrow', group: 'Header' },
    ...['services', 'booking', 'pricing', 'general'].flatMap(cat => {
      const items = faqDefaults.en[cat] || [];
      return items.flatMap((_, idx) => [
        { key: `${cat}.${idx}.q`, label: `${cat} Q${idx + 1}`, group: `FAQ — ${cat.charAt(0).toUpperCase() + cat.slice(1)}`, multiline: false },
        { key: `${cat}.${idx}.a`, label: `${cat} A${idx + 1}`, group: `FAQ — ${cat.charAt(0).toUpperCase() + cat.slice(1)}`, multiline: true },
      ]);
    }),
  ],
  gallery: [
    { key: 'subtitle', label: 'Page Eyebrow', group: 'Header' },
    { key: 'description', label: 'Page Description', group: 'Header', multiline: true },
    { key: 'comingSoon', label: 'Coming Soon Text', group: 'Footer' },
  ],
  book: [
    { key: 'subtitle', label: 'Page Eyebrow', group: 'Header' },
    { key: 'success.message', label: 'Success Message', group: 'Confirmation', multiline: true },
  ],
  login: [
    { key: 'subtitle', label: 'Login Subtitle', group: 'Header' },
  ],
  register: [
    { key: 'subtitle', label: 'Register Subtitle', group: 'Header' },
  ],
};

const PAGE_KEYS = Object.keys(MARKETING_FIELDS);

// Navigate a nested object by dot-separated path
function getNestedValue(obj, path) {
  return path.split('.').reduce((o, k) => o?.[k], obj);
}

function getDefault(page, key, lang) {
  if (page === 'faq' && key !== 'subtitle') {
    const parts = key.split('.');
    return faqDefaults[lang]?.[parts[0]]?.[parseInt(parts[1])]?.[parts[2]] || '';
  }
  // Phone numbers map to business.phone / business.phone2
  if (page === 'contact' && key === 'phone1') {
    return (lang === 'fr' ? fr : en).business.phone;
  }
  if (page === 'contact' && key === 'phone2') {
    return (lang === 'fr' ? fr : en).business.phone2;
  }
  const pageMap = { home: 'home', services: 'services', about: 'about', contact: 'contact',
    faq: 'faq', gallery: 'gallery', book: 'booking', login: 'auth.login', register: 'auth.register' };
  const root = lang === 'fr' ? fr : en;
  const pageRoot = getNestedValue(root, pageMap[page]);
  return getNestedValue(pageRoot, key) || '';
}

export default function MarketingCopyEditor() {
  const { t } = useLanguage();
  const { refreshOverrides } = useMarketingCopy();
  const [activePage, setActivePage] = useState('home');
  const [dbOverrides, setDbOverrides] = useState({});
  const [forms, setForms] = useState({});
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState({ type: '', text: '' });
  const [openGroup, setOpenGroup] = useState(null);

  const loadOverrides = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/marketing-copy/admin`);
      const map = {};
      for (const row of res.data) {
        map[`${row.page}|${row.block_key}`] = { id: row.id, en: row.content_en, fr: row.content_fr };
      }
      setDbOverrides(map);
    } catch (err) { /* ignore */ }
  }, []);

  useEffect(() => { loadOverrides(); }, [loadOverrides]);

  // Reset forms when page changes
  useEffect(() => {
    setForms({});
    setOpenGroup(null);
    setMsg({ type: '', text: '' });
  }, [activePage]);

  const getFormValue = (key, lang) => {
    const formKey = `${key}|${lang}`;
    if (forms[formKey] !== undefined) return forms[formKey];
    const dbVal = dbOverrides[`${activePage}|${key}`];
    return dbVal?.[lang] || '';
  };

  const setFormValue = (key, lang, value) => {
    setForms(prev => ({ ...prev, [`${key}|${lang}`]: value }));
  };

  const isCustomized = (key) => {
    return !!dbOverrides[`${activePage}|${key}`];
  };

  const hasUnsavedChanges = (key) => {
    const enForm = forms[`${key}|en`];
    const frForm = forms[`${key}|fr`];
    return enForm !== undefined || frForm !== undefined;
  };

  const handleRevert = (key) => {
    // Clear form values and mark for deletion
    setForms(prev => ({ ...prev, [`${key}|en`]: '', [`${key}|fr`]: '' }));
  };

  const handleSave = async () => {
    setSaving(true);
    setMsg({ type: '', text: '' });

    try {
      const fields = MARKETING_FIELDS[activePage] || [];
      const items = [];

      for (const field of fields) {
        const enVal = getFormValue(field.key, 'en');
        const frVal = getFormValue(field.key, 'fr');

        // Only include fields that have values or had overrides (to handle reverts)
        if (enVal || frVal || isCustomized(field.key)) {
          items.push({
            page: activePage,
            block_key: field.key,
            content_en: enVal,
            content_fr: frVal,
          });
        }
      }

      if (items.length > 0) {
        await axios.put(`${API}/marketing-copy/admin`, { items });
      }

      setForms({});
      await loadOverrides();
      await refreshOverrides();
      setMsg({ type: 'success', text: t.admin.marketing.saveSuccess });
    } catch (err) {
      setMsg({ type: 'error', text: t.admin.marketing.saveError });
    } finally {
      setSaving(false);
    }
  };

  const fields = MARKETING_FIELDS[activePage] || [];
  const groups = [...new Set(fields.map(f => f.group))];

  return (
    <div>
      <h2 style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: '2rem', color: '#E8ECF0', fontWeight: 300, marginBottom: '0.5rem' }}>
        {t.admin.marketing.title}
      </h2>
      <p style={{ color: '#94A3B8', fontSize: '0.85rem', marginBottom: '2rem' }}>
        {t.admin.marketing.description}
      </p>

      {/* Page selector pills */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '2rem' }}>
        {PAGE_KEYS.map(page => (
          <button
            key={page}
            onClick={() => setActivePage(page)}
            style={{
              background: activePage === page ? '#D42B2B' : 'transparent',
              border: activePage === page ? '1px solid #D42B2B' : '1px solid rgba(148,163,184,0.25)',
              color: activePage === page ? '#0C1220' : '#94A3B8',
              padding: '0.5rem 1.2rem',
              fontFamily: "'Barlow', sans-serif",
              fontSize: '0.7rem',
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              cursor: 'pointer',
              transition: 'all 0.2s',
              fontWeight: activePage === page ? 600 : 400,
            }}
          >
            {t.admin.marketing.pages[page] || page}
          </button>
        ))}
      </div>

      {msg.text && (
        <div style={{
          padding: '0.8rem 1rem',
          marginBottom: '1.5rem',
          background: msg.type === 'success' ? 'rgba(34,197,94,0.1)' : 'rgba(239,68,68,0.1)',
          border: `1px solid ${msg.type === 'success' ? 'rgba(34,197,94,0.3)' : 'rgba(239,68,68,0.3)'}`,
          color: msg.type === 'success' ? '#4ade80' : '#f87171',
          fontSize: '0.85rem',
        }}>
          {msg.text}
        </div>
      )}

      {/* Accordion groups */}
      {groups.map(group => {
        const groupFields = fields.filter(f => f.group === group);
        const isOpen = openGroup === group;
        const groupHasOverrides = groupFields.some(f => isCustomized(f.key));

        return (
          <div key={group} style={{ marginBottom: '1px', background: 'rgba(148,163,184,0.05)' }}>
            <button
              onClick={() => setOpenGroup(isOpen ? null : group)}
              style={{
                width: '100%',
                background: '#111827',
                border: 'none',
                borderBottom: '1px solid rgba(148,163,184,0.1)',
                padding: '1rem 1.5rem',
                textAlign: 'left',
                cursor: 'pointer',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                color: isOpen ? '#D42B2B' : '#E8ECF0',
              }}
            >
              <span style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: '1.1rem', fontWeight: 500, display: 'flex', alignItems: 'center', gap: '0.8rem' }}>
                {group}
                {groupHasOverrides && (
                  <span style={{ fontSize: '0.6rem', background: '#D42B2B', color: '#0C1220', padding: '0.15rem 0.5rem', fontFamily: "'Barlow', sans-serif", fontWeight: 600, letterSpacing: '0.05em' }}>
                    {t.admin.marketing.customized}
                  </span>
                )}
              </span>
              <span style={{ fontSize: '1.2rem' }}>{isOpen ? '−' : '+'}</span>
            </button>

            {isOpen && (
              <div style={{ background: '#0f1729', padding: '1.5rem' }}>
                {groupFields.map(field => {
                  const defaultEn = typeof getDefault(activePage, field.key, 'en') === 'string' ? getDefault(activePage, field.key, 'en') : '';
                  const defaultFr = typeof getDefault(activePage, field.key, 'fr') === 'string' ? getDefault(activePage, field.key, 'fr') : '';
                  const customized = isCustomized(field.key);
                  const changed = hasUnsavedChanges(field.key);

                  return (
                    <div key={field.key} style={{ marginBottom: '1.5rem', padding: '1rem', background: '#111827', border: '1px solid rgba(148,163,184,0.1)' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.8rem' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                          <span style={{ color: '#E8ECF0', fontSize: '0.8rem', fontFamily: "'Barlow', sans-serif", fontWeight: 500 }}>
                            {field.label}
                          </span>
                          <span style={{
                            fontSize: '0.55rem',
                            background: customized ? 'rgba(212,43,43,0.15)' : 'rgba(148,163,184,0.1)',
                            color: customized ? '#D42B2B' : '#94A3B8',
                            padding: '0.1rem 0.4rem',
                            fontFamily: "'Barlow', sans-serif",
                            fontWeight: 500,
                            letterSpacing: '0.05em',
                            textTransform: 'uppercase',
                          }}>
                            {customized ? t.admin.marketing.customized : t.admin.marketing.default}
                          </span>
                          {changed && <span style={{ fontSize: '0.55rem', color: '#f59e0b' }}>*</span>}
                        </div>
                        {customized && (
                          <button
                            onClick={() => handleRevert(field.key)}
                            style={{
                              background: 'transparent',
                              border: '1px solid rgba(148,163,184,0.2)',
                              color: '#94A3B8',
                              padding: '0.25rem 0.6rem',
                              fontSize: '0.6rem',
                              letterSpacing: '0.05em',
                              textTransform: 'uppercase',
                              cursor: 'pointer',
                              fontFamily: "'Barlow', sans-serif",
                            }}
                          >
                            {t.admin.marketing.revert}
                          </button>
                        )}
                      </div>

                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.8rem' }}>
                        {/* EN */}
                        <div>
                          <label style={{ display: 'block', fontSize: '0.55rem', letterSpacing: '0.1em', color: '#64748B', textTransform: 'uppercase', marginBottom: '0.3rem', fontFamily: "'Barlow', sans-serif" }}>
                            {t.admin.marketing.en}
                          </label>
                          {field.multiline ? (
                            <textarea
                              value={getFormValue(field.key, 'en')}
                              onChange={e => setFormValue(field.key, 'en', e.target.value)}
                              placeholder={defaultEn}
                              rows={3}
                              style={{
                                width: '100%', background: '#1a2332', border: '1px solid rgba(148,163,184,0.2)',
                                color: '#E8ECF0', padding: '0.6rem', fontSize: '0.8rem',
                                fontFamily: "'Barlow', sans-serif", outline: 'none', resize: 'vertical',
                                boxSizing: 'border-box',
                              }}
                            />
                          ) : (
                            <input
                              value={getFormValue(field.key, 'en')}
                              onChange={e => setFormValue(field.key, 'en', e.target.value)}
                              placeholder={defaultEn}
                              style={{
                                width: '100%', background: '#1a2332', border: '1px solid rgba(148,163,184,0.2)',
                                color: '#E8ECF0', padding: '0.6rem', fontSize: '0.8rem',
                                fontFamily: "'Barlow', sans-serif", outline: 'none',
                                boxSizing: 'border-box',
                              }}
                            />
                          )}
                        </div>

                        {/* FR */}
                        <div>
                          <label style={{ display: 'block', fontSize: '0.55rem', letterSpacing: '0.1em', color: '#64748B', textTransform: 'uppercase', marginBottom: '0.3rem', fontFamily: "'Barlow', sans-serif" }}>
                            {t.admin.marketing.fr}
                          </label>
                          {field.multiline ? (
                            <textarea
                              value={getFormValue(field.key, 'fr')}
                              onChange={e => setFormValue(field.key, 'fr', e.target.value)}
                              placeholder={defaultFr}
                              rows={3}
                              style={{
                                width: '100%', background: '#1a2332', border: '1px solid rgba(148,163,184,0.2)',
                                color: '#E8ECF0', padding: '0.6rem', fontSize: '0.8rem',
                                fontFamily: "'Barlow', sans-serif", outline: 'none', resize: 'vertical',
                                boxSizing: 'border-box',
                              }}
                            />
                          ) : (
                            <input
                              value={getFormValue(field.key, 'fr')}
                              onChange={e => setFormValue(field.key, 'fr', e.target.value)}
                              placeholder={defaultFr}
                              style={{
                                width: '100%', background: '#1a2332', border: '1px solid rgba(148,163,184,0.2)',
                                color: '#E8ECF0', padding: '0.6rem', fontSize: '0.8rem',
                                fontFamily: "'Barlow', sans-serif", outline: 'none',
                                boxSizing: 'border-box',
                              }}
                            />
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}

      {/* Save button */}
      <div style={{ marginTop: '2rem' }}>
        <button
          onClick={handleSave}
          disabled={saving}
          style={{
            background: '#D42B2B',
            color: '#0C1220',
            border: 'none',
            padding: '1rem 2.5rem',
            fontFamily: "'Barlow', sans-serif",
            fontSize: '0.7rem',
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            fontWeight: 600,
            cursor: saving ? 'not-allowed' : 'pointer',
            opacity: saving ? 0.7 : 1,
          }}
        >
          {saving ? t.admin.marketing.saving : t.admin.marketing.save}
        </button>
      </div>
    </div>
  );
}
